import React, { FC, JSX } from 'react';
import { StepperProps } from '../../types/components.d';

const Stepper: FC<StepperProps> = ({ steps, currentStep, onStepClick, progressTrackClassName, progressBarClassName, hideProgressBar }): JSX.Element => {
  const progressPercent = steps.length > 1 ? (currentStep / (steps.length - 1)) * 100 : 0;
  
  return (
    <div className="w-full max-w-full  pb-1">
      {/* Connector lines removed per request; only steps remain */}
      <div className="relative w-full overflow-x-auto scrollbar-hide min-w-0">
         <div className="grid grid-cols-2 md:flex md:items-start md:gap-3 md:flex-wrap md:flex-nowrap">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isClickable = onStepClick && index <= currentStep;

          return (
             <div key={index} className="mt-4 flex flex-col items-center shrink-0 min-w-[120px] sm:min-w-[220px]">
              {/* Step Circle */}
              <button
                type="button"
                className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-colors duration-200 ${
                  isCompleted
                    ? 'bg-green-600 border-green-600 text-white'
                    : isCurrent
                    ? 'bg-green-100 border-green-600 text-green-600'
                    : 'bg-white border-gray-300 text-gray-400'
                } ${isClickable ? 'cursor-pointer hover:border-green-400' : 'cursor-default'}`}
                onClick={() => isClickable && onStepClick && onStepClick(index)}
              >
                {isCompleted ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <span className="text-xs sm:text-sm font-medium">{index + 1}</span>
                )}
              </button>

               {/* Step Content */}
               <div className="mt-2 text-center px-1 w-full">
                 <p className={`text-[10px] sm:text-sm lg:text-base font-medium leading-tight break-words ${isCompleted || isCurrent ? 'text-green-600' : 'text-gray-400'}`}>
                   {step.title}
                 </p>
               </div>
            </div>
          );
        })}
        </div>
      </div>

      {!hideProgressBar && (
        <div className="mt-4">
          <div className={`w-full rounded-full h-2 ${progressTrackClassName || 'bg-gray-200'}`}>
            <div
              className={`h-2 rounded-full transition-all duration-300 ease-out ${progressBarClassName || 'bg-green-600'}`}
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between mt-2 text-[11px] sm:text-xs text-gray-500 gap-1">
            <span>Step {currentStep + 1} of {steps.length}</span>
            <div className='flex-grow'></div>
            <span className="sm:text-right">{Math.round(((currentStep + 1) / steps.length) * 100)}% Complete</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stepper;
